import React, { useState, useEffect } from 'react';
import { getDatabase, ref, onValue } from 'firebase/database';
import './ComplianceLogs.css';

const ComplianceLogs = () => {
    const [complianceLogs, setComplianceLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedLog, setExpandedLog] = useState(null);

    useEffect(() => {
        const db = getDatabase();
        const logsRef = ref(db, 'ComplianceLogs');

        const unsubscribe = onValue(logsRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const logsArray = Object.entries(data).map(([key, value]) => ({
                    id: key,
                    ...value
                }));
                // Sort by timestamp descending (newest first)
                logsArray.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
                setComplianceLogs(logsArray);
            } else {
                setComplianceLogs([]);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const formatDate = (isoString) => {
        const date = new Date(isoString);
        return date.toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const filteredLogs = complianceLogs.filter(log => 
        log.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.userId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.ipAddress?.includes(searchTerm)
    );

    const toggleExpand = (logId) => {
        setExpandedLog(expandedLog === logId ? null : logId);
    };

    if (loading) {
        return (
            <div className="compliance-logs-container">
                <div className="compliance-logs-loading">
                    <div className="compliance-logs-spinner"></div>
                    <span>Loading compliance logs...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="compliance-logs-container">
            <div className="compliance-logs-header">
                <div className="compliance-logs-title-section">
                    <h2>Compliance Logs</h2>
                    <span className="compliance-logs-count">{complianceLogs.length} records</span>
                </div>
                <div className="compliance-logs-search">
                    <input
                        type="text"
                        placeholder="Search by email, user ID or IP..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="compliance-search-input"
                    />
                </div>
            </div>

            {filteredLogs.length === 0 ? (
                <div className="compliance-logs-empty">
                    <span className="compliance-empty-icon">📋</span>
                    <p>No compliance logs found</p>
                </div>
            ) : (
                <div className="compliance-logs-list">
                    {filteredLogs.map((log) => (
                        <div 
                            key={log.id} 
                            className={`compliance-log-card ${expandedLog === log.id ? 'expanded' : ''}`}
                        >
                            <div 
                                className="compliance-log-summary"
                                onClick={() => toggleExpand(log.id)}
                            >
                                <div className="compliance-log-main">
                                    <div className="compliance-log-user">
                                        <span className="compliance-user-email">{log.userEmail || 'Unknown'}</span>
                                        <span className="compliance-user-id">{log.userId}</span>
                                        {log.type === 'data_connector_rights' && (
                                            <span className="compliance-user-id">Data source: {log.connectorName} → {log.host}</span>
                                        )}
                                    </div>
                                    <div className="compliance-log-meta">
                                        <span className="compliance-log-date">{formatDate(log.timestamp)}</span>
                                        <span className="compliance-log-ip">{log.ipAddress}</span>
                                    </div>
                                </div>
                                <div className="compliance-log-status">
                                    {log.acceptedCompliance ? (
                                        <span className="compliance-badge accepted">✓ Accepted</span>
                                    ) : (
                                        <span className="compliance-badge pending">Pending</span>
                                    )}
                                    <span className="compliance-expand-icon">
                                        {expandedLog === log.id ? '▲' : '▼'}
                                    </span>
                                </div>
                            </div>

                            {expandedLog === log.id && (
                                <div className="compliance-log-details">
                                    <div className="compliance-detail-grid">
                                        <div className="compliance-detail-item">
                                            <label>User ID</label>
                                            <span>{log.userId}</span>
                                        </div>
                                        <div className="compliance-detail-item">
                                            <label>Email</label>
                                            <span>{log.userEmail}</span>
                                        </div>
                                        <div className="compliance-detail-item">
                                            <label>Timestamp</label>
                                            <span>{log.timestamp}</span>
                                        </div>
                                        <div className="compliance-detail-item">
                                            <label>IP Address</label>
                                            <span>{log.ipAddress}</span>
                                        </div>
                                        <div className="compliance-detail-item">
                                            <label>Text Version</label>
                                            <span>{log.complianceTextVersion}</span>
                                        </div>
                                        <div className="compliance-detail-item">
                                            <label>Text Hash</label>
                                            <span className="compliance-hash">{log.complianceTextHash}</span>
                                        </div>
                                        <div className="compliance-detail-item full-width">
                                            <label>User Agent</label>
                                            <span className="compliance-useragent">{log.userAgent}</span>
                                        </div>
                                        {log.type === 'data_connector_rights' && (
                                            <div className="compliance-detail-item full-width">
                                                <label>Data Source</label>
                                                <span>{log.connectorName} ({log.connectorId}) · {log.host} · account {log.worldId}</span>
                                            </div>
                                        )}
                                        {log.type !== 'data_connector_rights' && (
                                            <div className="compliance-detail-item full-width">
                                                <label>Terms Accepted</label>
                                                <span>{log.acceptedTerms ? '✓ Yes' : '✗ No'}</span>
                                            </div>
                                        )}
                                        <div className="compliance-detail-item full-width">
                                            <label>Compliance Accepted</label>
                                            <span>{log.acceptedCompliance ? '✓ Yes' : '✗ No'}</span>
                                        </div>
                                    </div>
                                    
                                    {log.fullComplianceText && (
                                        <div className="compliance-text-section">
                                            <label>Accepted Compliance Text</label>
                                            <pre className="compliance-full-text">{log.fullComplianceText}</pre>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ComplianceLogs;

